import cx from 'classnames';
import {useTranslations} from 'next-intl';
import Container from '@/container';
import {Row, Col} from '@/grid';
import {Grid} from '@/content/features/shared';
import {SectionPaddingHalf} from '@/section';
import {H2, P} from '@/typography';
import {withBgNeutral200, withNeutral700, withNeutral900} from '@/hocs/withColors';
import {withHeadingEyebrow, withUppercase} from '@/hocs/withFontType';
import {withMarginBottom2xlToLg} from '@/hocs/withSpacing';
import StatsCard from '@/ui/stats-card';
import LinkComponent from '@/ui/links';
import {BreakVisibleInDesktop} from '@/ui/breaks';
import styles from './styles.module.scss';

const BgSectionPaddingHalf = withBgNeutral200(SectionPaddingHalf);
const EyebrowNeutral700 = withNeutral700(withUppercase(withHeadingEyebrow(P)));
const H2Neutral900 = withMarginBottom2xlToLg(withNeutral900(H2));

const StoriesSection = () => {
  const t = useTranslations('customers.storiesSection');

  return (
    <BgSectionPaddingHalf className={styles.section}>
      <Container>
        <Grid fluid>
          <Row>
            <Col xs={12}>
              <EyebrowNeutral700>{t('eyebrow')}</EyebrowNeutral700>
            </Col>
            <Col xs={12} md={10}>
              <H2Neutral900>{t('title')}</H2Neutral900>
            </Col>
          </Row>
        </Grid>
        <Grid fluid>
          <Row>
            <Col xs={12}>
        <div className={styles['cards-grid']}>
          <StatsCard
            header={{companyName: t('card1.companyName'), industry: t('card1.industry')}}
            bgImage="/static/images/customers/statbg.jpg"
            footer={
              <>
                <div className={styles['card-progress-wrapper']}>
                  <span>{t('card1.progressFrom')}</span>
                  <span className={styles['card-progress']} />
                  <span>{t('card1.progressTo')}</span>
                </div>
                <LinkComponent
                  href={t('card1.ctaUrl')}
                  target="_blank"
                  variant="cta-default-xs-light">
                  {t('readStory')}
                </LinkComponent>
              </>
            }>
            <span className={cx(styles['card-stat--large'], styles['card-stat--neutral50'])}>
              {t('card1.stat')}
            </span>
            <p className={`heading-3 ${styles['card-text--light']}`}>
              {t.rich('card1.heading', {br: () => <BreakVisibleInDesktop />})}
            </p>
            <p className={styles['card-text--light']}>{t('card1.description')}</p>
          </StatsCard>
          <StatsCard
            header={{
              companyName: t('card2.companyName'),
              companyVariant: 'neutral700',
              industry: t('card2.industry')
            }}
            footer={
              <LinkComponent href={t('card2.ctaUrl')} target="_blank" variant="cta-default-xs">
                {t('readStory')}
              </LinkComponent>
            }>
            <span className={cx(styles['card-stat--medium'], styles['card-stat--green700'])}>
              {t('card2.stat')}
            </span>
            <p className={styles['card-text--neutral700']}>{t('card2.description')}</p>
          </StatsCard>
          <StatsCard
            header={{
              companyName: t('card3.companyName'),
              companyVariant: 'neutral700',
              industry: t('card3.industry')
            }}
            footer={
              <LinkComponent href={t('card3.ctaUrl')} target="_blank" variant="cta-default-xs">
                {t('readStory')}
              </LinkComponent>
            }>
            <span className={cx(styles['card-stat--medium'], styles['card-stat--green700'])}>
              {t('card3.stat')}
            </span>
            <p className={styles['card-text--neutral700']}>{t('card3.description')}</p>
          </StatsCard>
          <StatsCard
            header={{
              companyName: t('card4.companyName'),
              industry: t('card4.industry'),
              industryVariant: 'green'
            }}
            bgColor="#012620"
            footer={
              <LinkComponent
                href={t('card4.ctaUrl')}
                target="_blank"
                variant="cta-default-xs-light">
                {t('readStory')}
              </LinkComponent>
            }>
            <span className={cx(styles['card-stat--medium'], styles['card-stat--green300'])}>
              {t('card4.stat')}
            </span>
            <p className={styles['card-text--light']}>{t('card4.description')}</p>
          </StatsCard>
        </div>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
        <div className={styles['cards-grid--row2']}>
          <StatsCard
            header={{
              companyName: t('card5.companyName'),
              companyVariant: 'neutral700',
              industry: t('card5.industry')
            }}
            footer={
              <LinkComponent href={t('card5.ctaUrl')} target="_blank" variant="cta-default-xs">
                {t('readStory')}
              </LinkComponent>
            }>
            <span className={cx(styles['card-stat--medium'], styles['card-stat--green700'])}>
              {t('card5.stat')}
            </span>
            <p className={styles['card-text--neutral700']}>{t('card5.description')}</p>
          </StatsCard>
          <StatsCard
            header={{
              companyName: t('card6.companyName'),
              companyVariant: 'neutral700',
              industry: t('card6.industry')
            }}
            bgColor="#efece7"
            footer={
              <LinkComponent href={t('card6.ctaUrl')} target="_blank" variant="cta-default-xs">
                {t('readStory')}
              </LinkComponent>
            }>
            <div className={styles['card-stats-row']}>
              <div className={styles['card-stat-col']}>
                <span className={cx(styles['card-stat--medium'], styles['card-stat--green700'])}>
                  {t('card6.stat')}
                </span>
                <p className={styles['card-text--neutral700']}>{t('card6.description')}</p>
              </div>
              <div className={styles['card-stat-divider']} />
              <div className={styles['card-stat-col']}>
                <span className={cx(styles['card-stat--small'], styles['card-stat--neutral900'])}>
                  {t('card6.stat2')}
                </span>
                <p className={styles['card-text--neutral700']}>{t('card6.description2')}</p>
              </div>
            </div>
          </StatsCard>
          <StatsCard
            header={{
              companyName: t('card7.companyName'),
              companyVariant: 'neutral700',
              industry: t('card7.industry')
            }}
            footer={
              <LinkComponent href={t('card7.ctaUrl')} target="_blank" variant="cta-default-xs">
                {t('readStory')}
              </LinkComponent>
            }>
            <span className={cx(styles['card-stat--medium'], styles['card-stat--green700'])}>
              {t('card7.stat')}
            </span>
            <p className={styles['card-text--neutral700']}>{t('card7.description')}</p>
          </StatsCard>
        </div>
            </Col>
          </Row>
        </Grid>
        <div className={styles['see-all-cta']}>
          <LinkComponent href={t('seeAllStoriesUrl')} target="_blank" variant="cta-default-xs">
            {t('seeAllStories')}
          </LinkComponent>
        </div>
      </Container>
    </BgSectionPaddingHalf>
  );
};

export default StoriesSection;
